/**
 * Форма реквизитов организации (company-profile-and-contacts). Фаза 2 (design D1,
 * _РЕШЕНИЯ_PM_фаза2.md A1): реквизиты правит и клиент-компания (вкл. ИНН/ОГРН, без
 * модерации), и команда — используется на обеих сторонах. Поля «профиль отрасли» и
 * «есть ОПО» остаются менеджерскими: у клиента скрыты и не отправляются
 * (`hideManagerFields`); сервер их всё равно проигнорирует при клиентской правке.
 * ОКОГУ — видимое необязательное поле (§3.2). Базовая фронт-валидация (validation.ts)
 * поверх серверной: при 422 показываем сообщения по полям из конверта ошибок.
 * Телефон организации (+7) — видимое поле.
 */
import { useState, type FormEvent } from 'react'
import { cn } from '@/lib/utils'
import type { CompanyProfile, DaDataPartyMatch, UpdateCompanyProfileInput } from '@/lib/api'
import { getErrorMessage, fieldError } from '@/lib/errors'
import {
  isValidInn,
  isLegalEntityInn,
  isValidLegalAddress,
  PROFILE_MESSAGES,
  validateProfile,
  validateExtraRequisites,
  type ExtraRequisiteRequirement,
  type ProfileDraft,
  type ProfileErrors,
} from '@/lib/validation'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Button } from '@/design-system/components/primitives/button'
import { Switch } from '@/design-system/components/primitives/switch'
import { Alert } from '@/design-system/components/feedback/alert'
import { Hint } from '@/design-system/components/primitives/text'
import { normalizePersonName, normalizePositionName } from '@/lib/name-case'

/**
 * Применяет найденную DaData организацию к черновику формы (design D5, §3.3 п.5):
 * заполняются ТОЛЬКО присутствующие в ответе поля — если DaData поле не вернула (пустая
 * строка/undefined), уже введённое пользователем значение не затирается.
 */
function applyDaDataMatch(draft: ProfileDraft, match: DaDataPartyMatch): ProfileDraft {
  const next = { ...draft }
  const keys: (keyof ProfileDraft & keyof DaDataPartyMatch)[] = [
    'nameFull',
    'nameShort',
    'inn',
    'kpp',
    'ogrn',
    'okpo',
    'oktmo',
    'okved',
    'okogu',
    'legalAddress',
    'directorName',
    'directorPosition',
  ]
  for (const key of keys) {
    const value = match[key]
    if (value) next[key] = value
  }
  return next
}

/**
 * Подсказка по адресу, подставленному из DaData (issue #10): пустой результат — адрес
 * пригоден к сохранению. Отдельный текст про индекс нужен потому, что DaData у части
 * организаций отдаёт адрес без почтового индекса, а сохранение требует его.
 */
function daDataAddressHint(legalAddress?: string): string | null {
  const addr = (legalAddress ?? '').trim()
  if (!addr || isValidLegalAddress(addr)) return null
  if (!/\b\d{6}\b/.test(addr)) {
    return 'DaData вернула адрес без почтового индекса — допишите индекс в поле «Юридический адрес».'
  }
  return `Проверьте юридический адрес из DaData: ${PROFILE_MESSAGES.legalAddress.toLowerCase()}.`
}

function toDraft(p: CompanyProfile): ProfileDraft {
  return {
    nameFull: p.nameFull,
    nameShort: p.nameShort,
    inn: p.inn,
    kpp: p.kpp,
    ogrn: p.ogrn,
    okpo: p.okpo,
    oktmo: p.oktmo,
    okved: p.okved,
    okogu: p.okogu ?? '',
    legalAddress: p.legalAddress,
    email: p.email,
    phone: p.phone ?? '',
    directorName: p.directorName,
    directorPosition: p.directorPosition,
  }
}

/**
 * Placeholder-примеры реквизитов (IC-3, п.4; дословно из прототипа `sout.html:1042-1080`).
 * Показываются ТОЛЬКО в СОУТ-контексте (когда задан `requireExtraRequisites`) — чтобы не
 * менять внешний вид формы профиля вне СОУТ.
 */
const REQUISITE_PLACEHOLDERS: Partial<Record<keyof ProfileDraft, string>> = {
  inn: '10 или 12 цифр',
  kpp: '9 цифр',
  ogrn: '13 или 15 цифр',
  okpo: '8 или 10 цифр',
  okogu: 'Если знаете',
  okved: 'Например: 85.42.9',
  oktmo: '8 или 11 цифр',
}

export function CompanyProfileForm({
  profile,
  onSave,
  hideManagerFields = false,
  requireExtraRequisites,
  onDaDataLookup,
}: {
  profile: CompanyProfile
  onSave: (input: UpdateCompanyProfileInput) => Promise<CompanyProfile>
  /**
   * Клиент-компания (design D1): «профиль отрасли» и «есть ОПО» — менеджерские поля,
   * скрыты и не отправляются в теле запроса.
   */
  hideManagerFields?: boolean
  /**
   * СОУТ-скоупная обязательность ОКПО/ОКВЭД/ОКТМО (IC-3, реш.4): визард СОУТ передаёт
   * `{ okpo: true, okved: true, oktmo: true }`. Проверка накладывается ПОВЕРХ
   * `validateProfile` (её саму не трогаем — иначе сломался бы онбординг вне СОУТ). Наличие
   * пропа также включает контекстные placeholder-примеры у полей реквизитов.
   */
  requireExtraRequisites?: ExtraRequisiteRequirement
  /**
   * Автозаполнение по ИНН через DaData (p2-dadata, §3.3, design D11) — необязательный
   * колбэк: клиентская страница передаёт `companyApi.dadataLookup`, командная карточка
   * клиента — `teamClientApi(cid).dadataLookup`. Без пропа кнопка не рендерится (вне
   * этих экранов форма используется, например, в мастерах заказов без автозаполнения).
   */
  onDaDataLookup?: (inn: string) => Promise<DaDataPartyMatch[]>
}) {
  const [draft, setDraft] = useState<ProfileDraft>(toDraft(profile))
  const [industryProfile, setIndustryProfile] = useState(profile.industryProfile ?? '')
  const [hasOpo, setHasOpo] = useState(profile.hasOpo)
  const [errors, setErrors] = useState<ProfileErrors>({})
  const [serverError, setServerError] = useState<unknown>(null)
  const [done, setDone] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [dadataLoading, setDadataLoading] = useState(false)
  const [dadataError, setDadataError] = useState<string | null>(null)
  const [dadataMatches, setDadataMatches] = useState<DaDataPartyMatch[] | null>(null)

  function set<K extends keyof ProfileDraft>(key: K, value: string) {
    setDraft((d) => ({ ...d, [key]: value }))
    setDone(false) // правка любого поля сбрасывает баннер «сохранено»
  }

  function err(key: keyof ProfileDraft): string | undefined {
    return errors[key] ?? fieldError(serverError, key)
  }

  function applyMatch(match: DaDataPartyMatch) {
    setDraft((d) => applyDaDataMatch(d, match))
    setDadataMatches(null)
    // issue #10: адрес из DaData подставляется как есть — ничего не выдумываем. Если в
    // ответе не оказалось почтового индекса (ни в `unrestricted_value`, ни в
    // `postal_code`), сразу говорим, что нужно дописать: иначе сохранение отклонит
    // подставленное системой значение без объяснения причины.
    setDadataError(daDataAddressHint(match.legalAddress))
    setDone(false)
  }

  async function lookupDaData() {
    if (!onDaDataLookup || !isValidInn(draft.inn)) return
    setDadataError(null)
    setDadataMatches(null)
    setDadataLoading(true)
    try {
      const matches = await onDaDataLookup(draft.inn)
      if (matches.length === 0) {
        setDadataError('Организация с таким ИНН не найдена')
      } else if (matches.length === 1) {
        applyMatch(matches[0])
      } else {
        setDadataMatches(matches)
      }
    } catch (e) {
      setDadataError(getErrorMessage(e, 'Не удалось выполнить автозаполнение по ИНН'))
    } finally {
      setDadataLoading(false)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setServerError(null)
    setDone(false)
    // СОУТ-скоупная обязательность (IC-3): накладываем ПОВЕРХ форматной validateProfile,
    // не меняя её. validateExtraRequisites добавляет ошибку только по пустому обязательному
    // полю (формат непустого-но-кривого уже отдаёт validateProfile).
    const localErrors: ProfileErrors = {
      ...validateProfile(draft),
      ...(requireExtraRequisites ? validateExtraRequisites(draft, requireExtraRequisites) : {}),
    }
    setErrors(localErrors)
    if (Object.keys(localErrors).length > 0) return
    setSubmitting(true)
    try {
      const payload: UpdateCompanyProfileInput = { ...draft }
      if (!hideManagerFields) {
        payload.industryProfile = industryProfile || undefined
        payload.hasOpo = hasOpo
      }
      const updated = await onSave(payload)
      setDraft(toDraft(updated))
      setDone(true)
    } catch (e2) {
      setServerError(e2)
    } finally {
      setSubmitting(false)
    }
  }

  // Placeholder-примеры реквизитов показываем только в СОУТ-контексте (наличие пропа) —
  // форма профиля вне СОУТ остаётся без примеров (IC-3, п.4).
  const showExamples = Boolean(requireExtraRequisites)
  // `wide` — визуальная раскладка (F2-03): поле на всю ширину строки грид-контейнера
  // (nw:col-span-2). Состав/названия/валидация полей не меняются — только ширина.
  // `required` уходит и в Field (звёздочка у подписи, common.required-fields), и в Input.
  const text = (label: string, key: keyof ProfileDraft, required = false, wide = false) => (
    <Field
      label={label}
      required={required}
      className={cn('my-0', wide && 'nw:col-span-2')}
      error={err(key)}
    >
      {(props) => (
        <Input
          {...props}
          value={draft[key]}
          onChange={(e) => set(key, e.target.value)}
          // ФИО руководителя — «Иванов Иван Иванович» на уходе с поля (names-and-positions-normalization).
          onBlur={key === 'directorName' ? () => set(key, normalizePersonName(draft[key])) : undefined}
          spellCheck={key === 'directorName' ? false : undefined}
          required={required}
          placeholder={showExamples ? REQUISITE_PLACEHOLDERS[key] : undefined}
        />
      )}
    </Field>
  )

  // ОКПО/ОКВЭД/ОКТМО обязательны только в СОУТ-контексте (IC-3) — звёздочка появляется вместе
  // с обязательностью.
  const req = (key: 'okpo' | 'okved' | 'oktmo') => Boolean(requireExtraRequisites?.[key])
  // КПП обязателен только у юрлица (ИНН = 10 цифр) — то же правило, что в `validateProfile`,
  // поэтому звёздочка у подписи появляется и исчезает вместе с ним.
  const kppRequired = isLegalEntityInn(draft.inn)

  return (
    <Card header="Реквизиты организации">
      <form onSubmit={submit} noValidate>
        {serverError ? (
          <Alert variant="danger" className="mb-4">
            {getErrorMessage(serverError, 'Не удалось сохранить реквизиты')}
          </Alert>
        ) : null}
        {done ? (
          <Alert variant="info" className="mb-4">
            Реквизиты сохранены.
          </Alert>
        ) : null}
        <Grid cols={2}>
          {/* Строка 1 (F2-03): ИНН + кнопка «Заполнить по ИНН» инлайн справа от поля (не
              отдельным блоком над сеткой) · КПП. */}
          <Field label="ИНН" required className="my-0" error={err('inn')}>
            {(props) => (
              <div className="flex flex-wrap items-center gap-3">
                <Input
                  {...props}
                  value={draft.inn}
                  onChange={(e) => set('inn', e.target.value)}
                  required
                  placeholder={showExamples ? REQUISITE_PLACEHOLDERS.inn : undefined}
                  className="min-w-0 flex-1"
                />
                {onDaDataLookup ? (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void lookupDaData()}
                      disabled={!isValidInn(draft.inn) || dadataLoading}
                    >
                      {dadataLoading ? 'Ищем в DaData…' : 'Заполнить по ИНН'}
                    </Button>
                    {!isValidInn(draft.inn) ? (
                      <Hint>Введите корректный ИНН (10 или 12 цифр)</Hint>
                    ) : null}
                  </>
                ) : null}
              </div>
            )}
          </Field>
          {text('КПП', 'kpp', kppRequired)}
          {onDaDataLookup && (dadataError || (dadataMatches && dadataMatches.length > 1)) ? (
            <div className="nw:col-span-2">
              {dadataError ? (
                <Alert variant="danger" className="mb-2">
                  {dadataError}
                </Alert>
              ) : null}
              {dadataMatches && dadataMatches.length > 1 ? (
                <div className="space-y-2" role="listbox" aria-label="Выберите организацию">
                  {dadataMatches.map((m, i) => (
                    <button
                      key={`${m.inn}-${m.kpp ?? i}`}
                      type="button"
                      role="option"
                      aria-selected={false}
                      className="w-full rounded-8 border border-line-strong p-3 text-left transition-colors hover:bg-hover"
                      onClick={() => applyMatch(m)}
                    >
                      <div className="text-14 font-medium text-txt">{m.nameFull}</div>
                      <div className="text-12 text-txt-secondary">
                        ИНН {m.inn}
                        {m.legalAddress ? ` · ${m.legalAddress}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {/* Строки 2-3: полное/сокращённое наименование — на всю ширину, сокращённое под
              полным (F2-03). */}
          {text('Полное наименование', 'nameFull', true, true)}
          {text('Сокращённое наименование', 'nameShort', false, true)}
          {/* Строка 4: ОГРН слева · ОКПО справа. */}
          {text('ОГРН', 'ogrn', true)}
          {text('ОКПО', 'okpo', req('okpo'))}
          {/* Строка 5: ОКОГУ слева · ОКВЭД справа. */}
          {text('ОКОГУ', 'okogu')}
          {text('ОКВЭД', 'okved', req('okved'))}
          {/* Строка 6: ОКТМО слева, правая часть пустая (второй элемент строки не добавляем —
              следующее full-width поле само перейдёт на новую строку grid-автораспределением). */}
          {text('ОКТМО', 'oktmo', req('oktmo'))}
          {/* Строка 7: юридический адрес — на всю ширину. */}
          {text('Юридический адрес', 'legalAddress', true, true)}
          {/* Подзаголовок «Руководитель организации» (F2-03) — визуальная группировка, не поле. */}
          <div className="mt-2 border-t border-line pt-4 text-13 font-medium text-txt nw:col-span-2">
            Руководитель организации
          </div>
          {/* Строка 8: ФИО руководителя слева · должность справа. */}
          {text('Руководитель', 'directorName', true)}
          <Field
            label="Должность руководителя"
            required
            className="my-0"
            error={err('directorPosition')}
          >
            {(props) => (
              // directorPosition — ВНЕ scope справочника должностей (p2-position-dictionary,
              // Non-Goals): остаётся свободным текстом (реквизит из ЕГРЮЛ, не штатная должность).
              <Input
                {...props}
                value={draft.directorPosition}
                onChange={(e) => set('directorPosition', e.target.value)}
                // Выписка ЕГРЮЛ приходит ЗАГЛАВНЫМИ — на уходе с поля «Генеральный директор».
                onBlur={() => set('directorPosition', normalizePositionName(draft.directorPosition))}
                spellCheck
                lang="ru"
                required
              />
            )}
          </Field>
          {/* Строка 9: официальный e-mail слева · телефон справа — на одной строке на
              десктопе, перенос на узком экране (grid-cols-1 ниже breakpoint `nw`). */}
          {text('Email для документов', 'email', true)}
          <Field label="Телефон" className="my-0" error={err('phone')}>
            {(props) => (
              <Input
                {...props}
                value={draft.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="+7 999 123-45-67"
                inputMode="tel"
              />
            )}
          </Field>
          {!hideManagerFields ? (
            <>
              <Field label="Профиль отрасли" className="my-0">
                {(props) => (
                  <Input
                    {...props}
                    value={industryProfile}
                    onChange={(e) => {
                      setIndustryProfile(e.target.value)
                      setDone(false)
                    }}
                    placeholder="Например, ЖКХ"
                  />
                )}
              </Field>
              <div className="flex items-center justify-between gap-3 py-2">
                <span className="text-13 text-txt-secondary">Есть ОПО</span>
                <Switch
                  checked={hasOpo}
                  onCheckedChange={(v) => {
                    setHasOpo(v)
                    setDone(false)
                  }}
                  aria-label="Есть ОПО"
                />
              </div>
            </>
          ) : null}
        </Grid>
        <Button type="submit" className="mt-4" disabled={submitting}>
          {submitting ? 'Сохранение…' : 'Сохранить реквизиты'}
        </Button>
      </form>
    </Card>
  )
}
